import { answerTheme, marqueSigne } from './quizTheme';
import { parseComplexAnswer, normalizeAnswer, jugerTrous } from '../../../utils/questionUtils';

/**
 * Correction sur place, la partie commune aux widgets à TUILES (QCM simple et
 * multiple, lot 1 du 2026-09-21) et au tableau (lot 3). Un widget reçoit
 * `corrige`, en déduit l'état de chacun de ses éléments, et pose le signe à côté.
 *
 * `corrige = { bonneReponse, reponseEleve, options }`, les deux réponses dans
 * l'encodage du widget lui-même : la valeur d'un choix (`getChoixValue`) pour un
 * QCM simple, un tableau JSON de valeurs pour un QCM multiple, la phrase avec
 * ses `{{trous}}` pour un texte à trous. C'est `resolveBonneReponse` (questionUtils)
 * qui produit la première, et `answers[part][id].réponse` qui porte la seconde :
 * on ne réinvente aucun encodage. `options` (lot 4) : les règles de jugement que
 * Quiz a appliquées (optionsDeFiche / optionsDeQuestion), pour les formats à
 * plusieurs trous qui affinent trou par trou avec la règle même du scoring.
 *
 * CONTRAT : `corrige` n'arrive au widget QUE SI LA RÉPONSE EST FAUSSE (c'est
 * Quiz, comme l'aperçu prof, qui le garantit). Un widget n'a donc jamais à
 * recomparer : Quiz a tranché avec toutes ses règles de tolérance (2,3 = 2,30,
 * écritures admises, expressions équivalentes), et un widget qui recomparerait
 * à sa façon pourrait dire le contraire. Ce qu'il a mis est faux ; ce qu'il
 * fallait mettre est `bonneReponse`. Les formats à plusieurs éléments affinent
 * élément par élément (une tuile cochée peut être juste dans un multiple faux,
 * un trou peut être juste dans une phrase fausse), toujours avec la fonction
 * même du scoring ; les formats à un seul champ n'ont rien à affiner.
 *
 * Les douze formats sont marqués depuis le 2026-09-21 (lots 1 à 6). La liste
 * `corrigeSurPlace` qui disait lesquels l'étaient, et la feuille du bas d'avant
 * qui redessinait la bonne réponse pour les autres, ont été supprimées au lot 7.
 */

/** Les valeurs d'une réponse, qu'elle soit simple ('84') ou multiple ('["27","51"]'). */
export const valeursDe = (reponse) => {
    if (reponse == null || reponse === '') return new Set();
    if (typeof reponse === 'string' && reponse.startsWith('[')) {
        try {
            const arr = JSON.parse(reponse);
            if (Array.isArray(arr)) return new Set(arr.map(String));
        } catch { /* pas un tableau : une valeur simple qui commence par [ */ }
    }
    return new Set([String(reponse)]);
};

/**
 * L'état d'une tuile de valeur `value`, ou `null` (tuile neutre, ou pas de
 * correction en cours). Les trois cas qui parlent à l'élève :
 *   juste   : il l'a cochée, et c'était bon ;
 *   faux    : il l'a cochée, et ce n'était pas ça ;
 *   attendu : il ne l'a pas cochée, et il fallait.
 */
export const etatTuile = (corrige, value) => {
    if (!corrige) return null;
    const bonne = valeursDe(corrige.bonneReponse).has(String(value));
    const cochee = valeursDe(corrige.reponseEleve).has(String(value));
    if (bonne && cochee) return 'juste';
    if (cochee) return 'faux';
    if (bonne) return 'attendu';
    return null;
};

/**
 * Tableau (Vrai/Faux, colonnes libres, bijection ; lot 3) : par ligne, l'index
 * de la colonne qu'il a cochée et celui de la colonne attendue, -1 s'il n'y en
 * a pas. Les deux réponses sont des chaînes `[ligne|colonne]…`, lues par le
 * parseur du scoring et rapprochées avec sa normalisation (casse, accents) :
 * l'ordre des lignes n'a pas d'importance, un « vrai » en minuscules non plus,
 * exactement ce que compareComplexAnswers a jugé. Jamais `config.bonnesReponses`,
 * que le widget reçoit sans s'en servir : la vérité marquée est celle du verdict.
 */
export const etatsTableau = (corrige, lignes, colonnes) => {
    if (!corrige) return null;
    const colonneParLigne = (reponse) => {
        const lue = new Map((parseComplexAnswer(reponse) || [])
            .map(({ phrase, reponse: colonne }) => [normalizeAnswer(phrase), normalizeAnswer(colonne)]));
        return lignes.map((ligne) => {
            const colonne = lue.get(normalizeAnswer(String(ligne)));
            return colonne == null ? -1 : colonnes.findIndex((c) => normalizeAnswer(String(c)) === colonne);
        });
    };
    const cochees = colonneParLigne(corrige.reponseEleve);
    const attendues = colonneParLigne(corrige.bonneReponse);
    return lignes.map((_, i) => ({ cochee: cochees[i], attendue: attendues[i] }));
};

/** L'état d'une case du tableau (sa ligne `etat`, sa colonne `col`), ou null pour une case neutre. */
export const etatCase = ({ cochee, attendue }, col) => {
    if (col === cochee) return col === attendue ? 'juste' : 'faux';
    return col === attendue ? 'attendu' : null;
};

/**
 * Texte à trous (lot 4) : par trou, ce qu'il a mis, ce qu'il fallait, et le
 * verdict de CE trou par la règle même du scoring (trouJuste, avec les options
 * de tolérance que Quiz a appliquées : 2,30 vaut 2,3 en question tolérante).
 * La réponse de l'élève est lue en GARDANT les trous vides : le scoring les
 * saute (extractTrouWords), ce qui décale tout d'un cran, et un trou vidé après
 * coup est possible (tapé, effacé, puis validé) ; ici chaque trou reste en face
 * du sien. Les mots rendus sont les mots bruts (« 4,473 »), la comparaison se
 * fait sur les mots normalisés, comme compareAnswers. Les trous SANS ORDRE
 * déclarés par la carte (options.interchangeables, 2026-09-29) sont jugés par
 * jugerTrous, comme le score : « 4 + 3 » pour « 3 + 4 » est vert, et un trou
 * faux du groupe montre la valeur restée libre, celle qu'il fallait écrire.
 */
const RE_TROU = /\{\{([^{}]*)\}\}/g;
const motsDesTrous = (str) => [...String(str || '').matchAll(RE_TROU)].map((m) => m[1].trim());

export const etatsTrous = (corrige) => {
    if (!corrige) return null;
    const attendus = motsDesTrous(corrige.bonneReponse);
    const mis = motsDesTrous(corrige.reponseEleve);
    const verdicts = jugerTrous(mis.map(normalizeAnswer), attendus.map(normalizeAnswer), corrige.options || {});
    return attendus.map((_, i) => ({
        mot: mis[i] ?? '',
        attendu: attendus[verdicts[i].attendu],
        juste: verdicts[i].juste,
    }));
};

const jsonOu = (str, defaut) => {
    try { const p = JSON.parse(str); return p ?? defaut; } catch { return defaut; }
};

/**
 * Relier (lot 5) : par source, la cible qu'il a reliée, la cible attendue, et
 * le verdict de CE lien, par égalité stricte des textes (le scoring compare
 * les paires triées telles quelles). Les deux réponses sont des tableaux JSON
 * de paires [source, cible]. Une source absente de sa réponse : cible null.
 */
export const etatsRelier = (corrige) => {
    if (!corrige) return null;
    const paires = (json) => jsonOu(json, []).filter(Array.isArray).map(([s, c]) => [String(s), String(c)]);
    const attendues = new Map(paires(corrige.bonneReponse));
    const reliees = new Map(paires(corrige.reponseEleve));
    const parSource = new Map();
    for (const [source, attendue] of attendues) {
        const cible = reliees.has(source) ? reliees.get(source) : null;
        parSource.set(source, { cible, attendue, juste: cible === attendue });
    }
    return parSource;
};

/**
 * Classer (lot 5) : par boîte, les étiquettes qu'il y a posées (juste si la
 * boîte est la bonne, sinon faux, avec la boîte attendue), et les fantômes :
 * les étiquettes attendues ici qu'il a posées ailleurs, à faire réapparaître
 * en pointillé. Les deux réponses sont des objets JSON { boîte: [étiquettes] }.
 */
export const etatsClasser = (corrige) => {
    if (!corrige) return null;
    const objet = (json) => { const o = jsonOu(json, {}); return typeof o === 'object' && !Array.isArray(o) ? o : {}; };
    const attendu = objet(corrige.bonneReponse);
    const mis = objet(corrige.reponseEleve);
    const boiteAttendue = new Map();
    for (const [boite, items] of Object.entries(attendu)) {
        for (const it of (Array.isArray(items) ? items : [])) if (!boiteAttendue.has(String(it))) boiteAttendue.set(String(it), boite);
    }
    const boites = {};
    for (const boite of new Set([...Object.keys(attendu), ...Object.keys(mis)])) {
        const posees = (Array.isArray(mis[boite]) ? mis[boite] : []).map((it) => {
            const item = String(it);
            const attendueIci = boiteAttendue.get(item) ?? null;
            return { item, juste: attendueIci === boite, attendue: attendueIci };
        });
        const dedans = new Set(posees.map((p) => p.item));
        const fantomes = (Array.isArray(attendu[boite]) ? attendu[boite] : []).map(String).filter((it) => !dedans.has(it));
        boites[boite] = { posees, fantomes };
    }
    return boites;
};

/**
 * Ordonner (lot 5) : par rang, ce qu'il a placé et si c'est le bon élément à
 * ce rang ; et la suite attendue en entier, parce qu'une case juste ne l'est
 * que par rapport à ses voisines. Les deux réponses sont des tableaux JSON.
 */
export const etatsOrdonner = (corrige) => {
    if (!corrige) return null;
    const liste = (json) => { const l = jsonOu(json, []); return Array.isArray(l) ? l.map(String) : []; };
    const attendus = liste(corrige.bonneReponse);
    const places = liste(corrige.reponseEleve);
    return {
        places: attendus.map((a, i) => ({ item: places[i] ?? '', juste: places[i] === a })),
        attendus,
    };
};

/**
 * Points sur une droite graduée ou dans un repère (lot 6) : par nom de point,
 * la valeur qu'il a posée (« 14,7 », « 2;24 »), la valeur attendue, juste ou
 * faux par égalité stricte des chaînes, comme le scoring qui compare les
 * objets JSON triés. Un point non posé : mis null, faux.
 */
export const etatsPoints = (corrige) => {
    if (!corrige) return null;
    const objet = (json) => { const o = jsonOu(json, {}); return typeof o === 'object' && !Array.isArray(o) ? o : {}; };
    const attendus = objet(corrige.bonneReponse);
    const mis = objet(corrige.reponseEleve);
    const parNom = new Map();
    for (const [nom, attendu] of Object.entries(attendus)) {
        const valeur = mis[nom] == null ? null : String(mis[nom]);
        parNom.set(nom, { mis: valeur, attendu: String(attendu), juste: valeur === String(attendu) });
    }
    return parNom;
};

/**
 * Programme de construction (lot 6) : par tâche, tracée ou non. Le widget
 * n'enregistre un tracé que s'il est juste (validé au relâcher contre la
 * géométrie attendue, avec la tolérance), donc une tâche présente dans sa
 * réponse est juste, une tâche absente (passée après deux échecs, ou abandon)
 * est fausse. Les clés « __extra » (tracés bonus automatiques) ne comptent pas.
 */
export const etatsConstruction = (corrige, tasks = []) => {
    if (!corrige) return null;
    const o = jsonOu(corrige.reponseEleve, {});
    const mis = typeof o === 'object' && !Array.isArray(o) ? o : {};
    return new Map(tasks.map((t) => [t.id, { juste: Boolean(mis[t.id]) }]));
};

/** L'étiquette « la réponse » : posée sur une tuile attendue, ou en légende sous un tableau. */
export const ETIQUETTE_ATTENDU = 'text-[10px] font-bold uppercase tracking-wide text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-full px-2 py-0.5';
/** La même pastille pour un CONTENU (le nom d'une source, en Relier) : jamais de capitales forcées, un x n'est pas un X. */
export const ETIQUETTE_CONTENU = 'text-[11px] font-bold text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-full px-2 py-0.5';

/**
 * La légende d'un tableau corrigé : une case attendue n'a pas la place d'une
 * étiquette, alors la légende la porte une fois pour toutes, sous le tableau.
 * Le carré a la classe `attendu` du thème, comme la case qu'il explique.
 */
export const LegendeAttendu = ({ clair = true, libelle = 'la réponse' }) => (
    <p className="mt-3 flex items-center justify-end gap-2" data-testid="legende-attendu">
        <span className={`inline-block w-4 h-4 rounded border-2 ${answerTheme(clair).attendu}`} aria-hidden="true" />
        <span className={ETIQUETTE_ATTENDU}>{libelle}</span>
    </p>
);

/**
 * Le signe posé sur une tuile marquée : une pastille ✓ ou ✗ à gauche pour ce
 * qu'il a coché, une étiquette à droite pour ce qu'il fallait cocher. Jamais
 * une rature sur le texte (un C rayé se lit « € »).
 */
export const MarqueCorrection = ({ etat, libelleAttendu = 'la réponse' }) => {
    if (etat === 'attendu') {
        return (
            <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${ETIQUETTE_ATTENDU}`}>
                {libelleAttendu}
            </span>
        );
    }
    if (etat === 'juste' || etat === 'faux') {
        return (
            <span
                className={`absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-black ${etat === 'juste' ? 'bg-emerald-500' : 'bg-red-500'}`}
                aria-label={etat}
            >
                {marqueSigne(etat)}
            </span>
        );
    }
    return null;
};

/** La tuile neutre en correction : l'aspect « pas cochée » d'aujourd'hui, sans survol ni enfoncement. */
export const TUILE_NEUTRE = {
    clair: 'border-slate-200 border-b-[5px] border-b-slate-300 bg-white',
    sombre: 'border-white/10 bg-white/5',
};
